import { SUBSCRIPTION_TYPES } from '@/constants/subscription-state';
import { isIosNativeApp } from '@/utils/deviceUtils';
import _ from 'lodash';
import moment from 'moment';
import Vue from 'vue';
import mobileMessagesUtils from '@/utils/mobileMessagesUtils';
import BillyApi from '../../api/BillyApi';
import CashflowSubscriptionApi from '../../api/CashflowSubscriptionApi';
import MetricsApi from '../../api/MetricsApi';
import SubscriptionStateApi from '../../api/SubscriptionStateApi';
import DDLogs from '../../DDLogs';
import PaymeInterface from '../../Payme';
import Segment from '../../Segment';

const SUBSCRIPTION_STEPS = {
  subscriptionType: 'choose-subscription-type',
  creditCard: 'credit-card',
  summary: 'summary',
  done: 'done',
};

const PLAN_TO_SUBSCRIPTION_TYPE = {
  annual: SUBSCRIPTION_TYPES.YEARLY,
  monthly: SUBSCRIPTION_TYPES.MONTHLY,
};

const CREDIT_CARD_FIELDS = {
  cardNumber: 'cardNumber',
  expiration: 'expiration',
  cvv: 'cvv',
  socialId: 'payerSocialId',
};

const PERSONAL_INFO_FIELDS = {
  firstName: 'payerFirstName',
  lastName: 'payerLastName',
  phoneNumber: 'payerPhone',
};

function _handlePaymentError(error, isUpdate) {
  const isKnownError = error && error.payload && error.payload.status_error_code;
  const errorDetails = isKnownError ? error.payload.status_error_details : 'unknown';
  const errorCode = isKnownError ? error.payload.status_error_code : undefined;
  const errorMessage = error && error.message ? error.message : undefined;

  if (isKnownError) {
    MetricsApi.sendCounter('payment.tokenize.error', { payme_status_error_code: errorCode });
  } else {
    MetricsApi.sendCounter('payment.tokenize.unknown-error');
  }

  if (isUpdate) {
    DDLogs.errorBusinessEvent('Update Subscription failed', { errorMessage, error });
    Segment.trackUserGot('UpdateCreditCardTokenizeError', {
      errorDetails,
      errorCode,
    });
  } else {
    DDLogs.errorBusinessEvent('Subscription failed', { errorMessage, error });
    Segment.trackUserGot('SubscriptionTokenizeError', {
      errorDetails,
      errorCode,
    });
  }
}

const state = {
  flowStep: null,
  fieldsInstance: null,
  fields: null,
  firstBillingDate: null,
  personalInfo: {
    [PERSONAL_INFO_FIELDS.firstName]: { value: null, isValid: false },
    [PERSONAL_INFO_FIELDS.lastName]: { value: null, isValid: false },
    [PERSONAL_INFO_FIELDS.phoneNumber]: { value: null, isValid: false },
  },
  creditCard: {
    [CREDIT_CARD_FIELDS.cardNumber]: { isEmpty: true, isValid: false, error: '' },
    [CREDIT_CARD_FIELDS.expiration]: { isEmpty: true, isValid: false, error: '' },
    [CREDIT_CARD_FIELDS.cvv]: { isEmpty: true, isValid: false, error: '' },
    [CREDIT_CARD_FIELDS.socialId]: { isEmpty: true, isValid: false, error: '' },
  },
  tokenizationError: null,
  creditCardIsValid: false,
  loadingToken: false,
  loadingBenefit: false,
  eligibilityError: null,
  fetchPricingError: null,
  paymeToken: null,
  didEnteredCcFields: false,
  type: null,
  currentPlanType: null,
  pricing: {
    annual: null,
    monthly: null,
    advisory: null,
  },
  enableTerms: false,
  cardNumberMask: null,
  nextPaymentDate: null,
  price: null,
  remainingIterations: null,
  failedPaymentsReasons: [],
  pendingCreditCardRetry: false,
  paymentProvider: null,
  payingMemberId: null,
  userCanceledSubscription: false,
  scheduledCancellationDate: null,
  haveInactivePaymeSubscription: null,
  subscriptionId: null,
};

function trackValidationFailures(cardIsValid) {
  if (!cardIsValid) {
    _.chain(state.creditCard)
      .pickBy(value => !value.isValid)
      .keys()
      .forEach(field => Segment.trackUserGot('CreditCardValidationFailed', { field }))
      .value();
  }
}

const getters = {
  SUBSCRIPTION_FLOW_STEPS: () => SUBSCRIPTION_STEPS,
  personalInfoFields: () => PERSONAL_INFO_FIELDS,
  creditCardFields: () => CREDIT_CARD_FIELDS,
  validPersonalInfo: state => _.every(_.map(_.values(PERSONAL_INFO_FIELDS), field => state.personalInfo[field].isValid)),
  subscriptionPrice: state => (state.type === SUBSCRIPTION_TYPES.YEARLY ? state.pricing.annual : state.pricing.monthly),
  subscriptionTypSelectedText: state => (state.type === SUBSCRIPTION_TYPES.YEARLY ? 'שנתי' : 'חודשי'),
  isYearlySubscription: state => state.type === SUBSCRIPTION_TYPES.YEARLY,
  isBiYearlySpecialOffering: state => state.type === SUBSCRIPTION_TYPES.YEARLY && state.price === 79800,
  totalMonthlyPricingPerYear: state => (state.pricing.monthly * 12).toFixed(0),
  yearlySavingAgainstMonthly: state => (state.pricing.monthly * 12 - state.pricing.annual).toFixed(0),
  yearlySavingAgainstMonthlyPercentage: state =>
    (((state.pricing.monthly * 12 - state.pricing.annual) / (state.pricing.monthly * 12)) * 100).toFixed(0),
  newFormattedCardNumberMask: state => {
    if (state.cardNumberMask) {
      const lastFourDigits = state.cardNumberMask.slice(-4);
      return `${lastFourDigits} ••••`;
    }
    return null;
  },
  formattedFirstBillingDate: state => moment(state.firstBillingDate).format('D.M.YYYY'),
  formattedNextPaymentDate: state => moment(state.nextPaymentDate).format('D.M.YYYY'),
  formattedFirstBillingDatePlusYear: state => moment(state.firstBillingDate).add(1, 'year').format('D.M.YYYY'),
  existingRemainingIterations: state => (state.remainingIterations && state.remainingIterations !== -1 ? state.remainingIterations : null),
  nextFormattedPaymentDate: (state, getters) => (getters.existingRemainingIterations
    ? moment(state.nextPaymentDate).add(state.remainingIterations, 'month').format('D.M.YYYY')
    : moment(state.nextPaymentDate).format('D.M.YYYY')),
  isMonthlySubscription: state => state.type === SUBSCRIPTION_TYPES.MONTHLY,
  subscriptionTypeText: (state, getters) => {
    if (getters.isBiYearlySpecialOffering) {
      return 'מנוי לשנתיים';
    }

    return getters.isYearlySubscription ? 'שנתי' : 'חודשי';
  },
  yearlySubscriptionOffering: (state, getters, rootState, rootGetters) => 'cashflow-annual-subscription',
  subscriptionOfferingId: (state, getters) => (getters.isYearlySubscription ? getters.yearlySubscriptionOffering : 'cashflow-monthly-subscription'),
  shouldShowFailedPaymentsBar: state => state.failedPaymentsReasons.length >= 2 && !state.pendingCreditCardRetry,
  shouldShowCreditCardUpdatedSuccessBar: state => state.failedPaymentsReasons.length >= 2 && state.pendingCreditCardRetry,
  isApplePaymentProvider: state => state.paymentProvider === 'apple' && !state.userCanceledSubscription,
  payingMemberId: state => state.payingMemberId,
  userCanceledSubscription: state => state.userCanceledSubscription,
  hasScheduledCancellation: state => !!state.scheduledCancellationDate,
  isWithinWeekBeforeCancellation: state => {
    if (!state.scheduledCancellationDate) {
      return false;
    }
    const daysUntil = moment(state.scheduledCancellationDate).startOf('day').diff(moment().startOf('day'), 'days');
    return daysUntil >= 0 && daysUntil <= 7;
  },
  isAppleProviderOrIosDevice: (state, getters) => (
    getters.isApplePaymentProvider
    || ((!state.subscriptionId || state.subscriptionId === 'NA') && isIosNativeApp())
  ),
  shouldUseApplePricingAndPayment: (state, getters) => getters.isAppleProviderOrIosDevice && !state.haveInactivePaymeSubscription,
};

const actions = {
  async fetchCreditCardMask({ commit }) {
    try {
      const creditCardDetails = await BillyApi.getCreditCardDetails();
      if (creditCardDetails) {
        commit('setCardNumberMask', creditCardDetails.cardNumberMask);
      }
    } catch (error) {
      commit('setCardNumberMask', undefined);
    }
  },
  setPersonalInfoField({ commit }, { field, value, isValid, errorMessage }) {
    commit('setPersonalInfoField', { field, value, isValid, errorMessage });
  },
  setCreditCardField({ commit }, { field, isEmpty, isValid }) {
    if (!state.didEnteredCcFields) {
      MetricsApi.sendCounter('subscription.payment-details.entered');
      commit('setDidEnteredCcFields', true);
    }
    commit('setCreditCardField', { field, isEmpty, isValid, error: '' });
  },
  setCreditCardValidations({ commit, state }) {
    if (!state.creditCard[CREDIT_CARD_FIELDS.cardNumber].isValid) {
      commit('setCreditCardValidationError', { field: CREDIT_CARD_FIELDS.cardNumber, error: 'אפשר לבדוק שוב את מספר הכרטיס?' });
    }
    if (!state.creditCard[CREDIT_CARD_FIELDS.expiration].isValid) {
      commit('setCreditCardValidationError', { field: CREDIT_CARD_FIELDS.expiration, error: 'התוקף לא נכון' });
    }
    if (!state.creditCard[CREDIT_CARD_FIELDS.cvv].isValid) {
      commit('setCreditCardValidationError', { field: CREDIT_CARD_FIELDS.cvv, error: '3 ספרות בגב הכרטיס' });
    }
    if (!state.creditCard[CREDIT_CARD_FIELDS.socialId].isValid) {
      commit('setCreditCardValidationError', { field: CREDIT_CARD_FIELDS.socialId, error: 'אפשר לבדוק שוב את מספר תעודת הזהות?' });
    }
    const cardIsValid = _.every(state.creditCard, field => field.isValid);
    commit('setCreditCardIsValid', cardIsValid);
    trackValidationFailures(cardIsValid);
    return cardIsValid;
  },
  async initPaymeInterface({ commit }) {
    const paymeInterface = await PaymeInterface.initOrGet();
    if (paymeInterface) {
      commit('setFieldsInstance', paymeInterface);
    }
  },
  async fetchFirstBillingDate({ commit, rootGetters }) {
    if (rootGetters['featureFlags/cashflowHomeBillingReadNextBillingDate']) {
      const { nextBillingDate } = await SubscriptionStateApi.fetchNextBillingDate();
      commit('setFirstBillingDate', { firstBillingDate: nextBillingDate });
      return;
    }
    const { firstBillingDate } = await SubscriptionStateApi.fetchFirstBillingDate();
    commit('setFirstBillingDate', { firstBillingDate });
  },
  async createSubscriptionV2({ state, dispatch, commit, getters, rootGetters }, { percentageDiscount }) {
    await dispatch('setCreditCardValidations');
    if (state.creditCardIsValid) {
      Segment.trackUserGot('SubscriptionSubmitSucceededOnValidation', { subscriptionVersion: 'v2' });
      try {
        commit('setLoadingToken', true);
        const paymeToken = await dispatch('getOrCreatePaymentToken');
        commit('setPaymeToken', paymeToken);
        Segment.trackUserGot('SubscriptionSubscribe');
        if (rootGetters['featureFlags/cashflowHomeBilling']) {
          const subscriptionType = state.type === SUBSCRIPTION_TYPES.YEARLY ? 'YEARLY' : 'MONTHLY';
          await CashflowSubscriptionApi.createSubscription({ subscriptionType, token: paymeToken.token });
        } else {
          await BillyApi.purchaseOffering({
            token: paymeToken.token,
            oneOffOfferings: [],
            subscriptionOfferings: [{ offeringId: getters.subscriptionOfferingId, numberOfInstallments: 1 }],
            bundleOfferings: [],
          });
        }
        MetricsApi.sendCounter('subscription.success');
        Segment.trackUserGot('SubscriptionSubscribeSucceeded');
        mobileMessagesUtils.sendAnalyticsEventToMobile('SubscriptionCreated_Marketing');
        commit('setStep', SUBSCRIPTION_STEPS.done);
        return true;
      } catch (error) {
        _handlePaymentError(error, false);
        commit('setTokenizationError', error);
        return false;
      } finally {
        commit('setLoadingToken', false);
      }
    } else {
      Segment.trackUserGot('SubscriptionSubmitFailedOnValidation', { validations: state.creditCard });
      return false;
    }
  },
  async toggleEnabledTerms({ state, dispatch, commit }) {
    await commit('setEnableTerms', !state.enableTerms);
  },

  async getOrCreatePaymentToken({ state, dispatch }) {
    return state.paymeToken ? state.paymeToken : await dispatch('createPaymentToken');
  },
  async updateCreditCard({ state, dispatch, commit, rootGetters }) {
    // Function is relevant only for payme and not other providers - as it uses V2
    await dispatch('setCreditCardValidations');
    // fetchSubscription resets `type` to the plan on record, which would discard a plan the customer
    // is part-way through selecting — this can run from the update flow's summary screen.
    const selectedType = state.type;
    await dispatch('fetchSubscription');
    if (selectedType) {
      commit('setType', selectedType);
    }
    if (state.creditCardIsValid) {
      Segment.trackUserGot('UpdateCreditCardSucceededOnValidation');
      try {
        commit('setLoadingToken', true);
        const { token, card } = await dispatch('createPaymentToken');
        Segment.trackUserGot('UpdatingCreditCard');
        if (rootGetters['featureFlags/cashflowHomeBilling']) {
          await CashflowSubscriptionApi.updateSubscription({ token });
        } else {
          await BillyApi.updateSubscriptionV2({
            paymentToken: token, card, iterationType: state.type,
          });
        }
        MetricsApi.sendCounter('subscription.success', { type: 'update' });
        Segment.trackUserGot('UpdatedCreditCardSucceeded');
      } catch (error) {
        _handlePaymentError(error, true);
        commit('setTokenizationError', error);
      } finally {
        commit('setLoadingToken', false);
      }
    } else {
      Segment.trackUserGot('UpdatedCreditCardFailedOnValidation', { validations: state.creditCard });
    }
  },
  async createPaymentToken({ state, rootState }) {
    const subscriptionPrice = rootState.pricing?.pricing?.monthly;
    const oneOffOfferingPrice = rootState.oneOffOfferings?.selectedOffering?.price?.amount;

    if (!subscriptionPrice && !oneOffOfferingPrice) {
      DDLogs.error('Payment token creation failed due to missing pricing', {
        pricing: rootState.pricing?.pricing,
        hasRootStatePricing: !!rootState.pricing,
        selectedOffering: rootState.oneOffOfferings?.selectedOffering,
        hasOneOffOfferingsState: !!rootState.oneOffOfferings,
        customerId,
      });
      throw new Error('Pricing not available for payment token creation');
    }

    const price = subscriptionPrice || oneOffOfferingPrice;

    const { customerId } = rootState.session;
    const sale = {
      payerFirstName: state.personalInfo[PERSONAL_INFO_FIELDS.firstName].value,
      payerLastName: state.personalInfo[PERSONAL_INFO_FIELDS.lastName].value,
      payerPhone: state.personalInfo[PERSONAL_INFO_FIELDS.phoneNumber].value.replace('-', ''),
      payerEmail: `${customerId}@riseup.co.il`,
      total: {
        label: 'תשלום לרייזאפ',
        amount: {
          currency: 'ILS',
          value: `${price}.00`,
        },
      },
    };
    Segment.trackUserGot('SubscriptionTokenize');
    const tokenRequest = await state.fieldsInstance.tokenize(sale);
    Segment.trackUserGot('SubscriptionTokenizeSucceeded');
    const { token, card } = tokenRequest;
    return { token, card };
  },
  async createLeadPaymentToken({ state }, {
    firstName, lastName, phone, email, price,
  }) {
    const sale = {
      payerFirstName: firstName,
      payerLastName: lastName,
      payerPhone: phone.replace('-', ''),
      payerEmail: email,
      total: {
        label: 'תשלום לרייזאפ',
        amount: {
          currency: 'ILS',
          value: `${price}.00`,
        },
      },
    };
    Segment.trackUserGot('LeadTokenize');
    const tokenRequest = await state.fieldsInstance.tokenize(sale);
    Segment.trackUserGot('LeadTokenizeSucceeded');
    const { token, card } = tokenRequest;
    return { token, card };
  },
  reset({ commit }) {
    commit('setTokenizationError', null);
  },
  async fetchInactivePaymeSubscription({ commit }) {
    try {
      const result = await BillyApi.getHaveInactivePaymeSubscription();
      commit('setHaveInactivePaymeSubscription', result.haveInactivePaymeSubscription);
    } catch (error) {
      DDLogs.error('Failed to fetch inactive payme subscription', { error });
      commit('setHaveInactivePaymeSubscription', false);
    }
  },
  async fetchPricing({ commit, state, getters, rootGetters, dispatch }) {
    try {
      const { pricing } = await BillyApi.fetchPricingOption();
      const shouldGetInactivePaymeSubscription = getters.isAppleProviderOrIosDevice;
      if (shouldGetInactivePaymeSubscription) {
        await dispatch('fetchInactivePaymeSubscription');
      }

      const shouldShowApplePricing = getters.shouldUseApplePricingAndPayment;
      if (shouldShowApplePricing) {
        pricing.annual = pricing.annualApple;
        pricing.monthly = pricing.monthlyApple;
      }

      commit('setPricing', pricing);
      commit('setFetchPricingError', null);
    } catch (error) {
      commit('setFetchPricingError', error);
      DDLogs.error('Failed to fetch pricing options', { error });
    }
  },
  async updateSubscriptionV2({ commit, rootGetters }) {
    // Function is relevant only for payme and not other providers - as it uses V2
    try {
      if (rootGetters['featureFlags/cashflowHomeBilling']) {
        const subscriptionType = state.type === SUBSCRIPTION_TYPES.YEARLY ? 'YEARLY' : 'MONTHLY';
        await CashflowSubscriptionApi.updateSubscription({ subscriptionType });
      } else {
        await BillyApi.updateSubscriptionV2({ iterationType: state.type });
      }
      commit('setStep', SUBSCRIPTION_STEPS.done);
      Segment.trackUserGot('updateSubscriptionSucceeded', { subscriptionVersion: 'v2' });
    } catch (error) {
      DDLogs.error('updateSubscriptionV2 error', { error });
    }
  },

  async fetchSubscription({ commit }) {
    try {
      const { subscription } = await BillyApi.getSubscription();
      commit('setNextPaymentDate', subscription.nextPaymentDate);
      commit('setType', PLAN_TO_SUBSCRIPTION_TYPE[subscription.planType]);
      commit('setCurrentPlanType', PLAN_TO_SUBSCRIPTION_TYPE[subscription.planType]);
      commit('setPrice', subscription.amount);
      commit('setRemainingIterations', subscription.remainingIterations);
      commit('setSubscriptionId', subscription.id);
      commit('setPaymentProvider', subscription.provider);
      commit('setPayingMemberId', subscription.payingMemberId); // Only for Apple subscription
      commit('setUserCanceledSubscription', subscription.userCanceledSubscription); // Only for Apple subscription
      commit('setScheduledCancellationDate', subscription.scheduledCancellationDate);
    } catch (error) {
      commit('setNextPaymentDate', undefined);
      // A stale plan would lock the wrong card in the update flow, so drop it rather than keep a
      // value this fetch could not confirm.
      commit('setCurrentPlanType', null);
    }
  },
  async fetchFailedPaymentsReasons({ commit }) {
    const data = await BillyApi.getFailedPaymentsReasons();
    commit('setFailedPaymentsReasons', data.failedPaymentsReasons || []);
    commit('setPendingCreditCardRetry', data.pendingCreditCardRetry || false);
  },
};

const mutations = {
  setCardNumberMask(state, cardNumberMask) {
    state.cardNumberMask = cardNumberMask;
  },
  setStep(state, flowStep) {
    state.flowStep = flowStep;
  },
  setPaymeToken(state, paymeToken) {
    state.paymeToken = paymeToken;
  },
  setDidEnteredCcFields(state, didEnteredCcFields) {
    state.didEnteredCcFields = didEnteredCcFields;
  },
  setFieldsInstance(state, { fieldsInstance, fields }) {
    state.fieldsInstance = fieldsInstance;
    state.fields = fields;
  },
  setFirstBillingDate(state, { firstBillingDate }) {
    // eslint-disable-next-line no-param-reassign
    state.firstBillingDate = firstBillingDate;
  },
  setPersonalInfoField(state, { field, value, isValid }) {
    Vue.set(state.personalInfo, field, { value, isValid });
  },
  setCreditCardField(state, { field, isEmpty, isValid, error }) {
    Vue.set(state.creditCard, field, { isEmpty, isValid, error });
  },
  setTokenizationError(state, error) {
    state.tokenizationError = error;
  },
  setEligibilityError(state, error) {
    state.eligibilityError = error;
  },
  setPersonalInfoValidationError(state, { field, error }) {
    Vue.set(state.personalInfo, field, _.extend({}, { error }, state.personalInfo[field]));
  },
  setCreditCardValidationError(state, { field, error }) {
    Vue.set(state.creditCard, field, _.extend({}, state.creditCard[field], { error }));
  },
  setCreditCardIsValid(state, isValid) {
    state.creditCardIsValid = isValid;
  },
  setLoadingToken(state, loading) {
    state.loadingToken = loading;
  },
  setLoadingBenefit(state, loading) {
    state.loadingBenefit = loading;
  },
  setType(state, type) {
    state.type = type;
  },
  // The plan the customer currently holds, as opposed to `type`, which the subscription flows
  // overwrite with the plan being selected.
  setCurrentPlanType(state, currentPlanType) {
    state.currentPlanType = currentPlanType;
  },
  setPrice(state, price) {
    state.price = price;
  },
  setPricing(state, pricing) {
    state.pricing = pricing;
  },
  setEnableTerms(state, enableTerms) {
    state.enableTerms = enableTerms;
  },
  setRemainingIterations(state, remainingIterations) {
    state.remainingIterations = remainingIterations;
  },
  setNextPaymentDate(state, nextPaymentDate) {
    state.nextPaymentDate = nextPaymentDate;
  },
  setFailedPaymentsReasons(state, reasons) {
    state.failedPaymentsReasons = reasons;
  },
  setPendingCreditCardRetry(state, pending) {
    state.pendingCreditCardRetry = pending;
  },
  setPaymentProvider(state, paymentProvider) {
    state.paymentProvider = paymentProvider;
  },
  setPayingMemberId(state, payingMemberId) {
    state.payingMemberId = payingMemberId;
  },
  setUserCanceledSubscription(state, userCanceledSubscription) {
    state.userCanceledSubscription = userCanceledSubscription;
  },
  setScheduledCancellationDate(state, scheduledCancellationDate) {
    state.scheduledCancellationDate = scheduledCancellationDate;
  },
  setFetchPricingError(state, fetchPricingError) {
    state.fetchPricingError = fetchPricingError;
  },
  setHaveInactivePaymeSubscription(state, haveInactivePaymeSubscription) {
    state.haveInactivePaymeSubscription = haveInactivePaymeSubscription;
  },
  setSubscriptionId(state, subscriptionId) {
    state.subscriptionId = subscriptionId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
